import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Pagination, PAGE_SIZE } from "@/components/Pagination";
import { Truck, Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { FilterBar } from "@/components/FilterBar";
import { api } from "@/lib/api";

const VIEWS = ["Cards", "Table"];
const SORTS = [
  ["name", "Name A–Z"],
  ["open_pos", "Most open orders"],
  ["slabs_in_stock", "Most slabs"],
];

export default function Suppliers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("name");
  const [view, setView] = useState("Cards");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    const { data } = await api.get("/suppliers/summary");
    setItems(data.items);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = !t
      ? [...items]
      : items.filter((s) =>
          [s.name, s.contact, s.phone, s.email, s.address, s.notes]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(t),
        );
    return list.sort((a, b) =>
      sort === "name"
        ? String(a.name).localeCompare(String(b.name))
        : (b[sort] || 0) - (a[sort] || 0),
    );
  }, [items, q, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const paged = filtered.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  const totals = useMemo(
    () => ({
      openPos: items.reduce((n, s) => n + s.open_pos, 0),
      slabs: items.reduce((n, s) => n + s.slabs_in_stock, 0),
      sinks: items.reduce((n, s) => n + s.sinks_on_hand, 0),
    }),
    [items],
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Inventory / Suppliers" />
      <main className="relative z-10 pb-28 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="suppliers-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Inventory
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Suppliers
              </h1>
              <p
                className="mt-1 text-sm text-zinc-500"
                data-testid="suppliers-count"
              >
                {items.length} supplier{items.length === 1 ? "" : "s"} ·{" "}
                {totals.openPos} open order
                {totals.openPos === 1 ? "" : "s"}
              </p>
            </div>
          </div>

          <div
            className="mt-5 flex flex-wrap gap-2 border-t border-zinc-800 pt-4"
            data-testid="suppliers-actions"
          >
            <Button
              data-testid="add-supplier-button"
              onClick={() => navigate("/inventory/suppliers/new")}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Plus size={15} weight="bold" className="mr-2" /> Add Supplier
            </Button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ["Suppliers", items.length, "text-white"],
            ["Open Orders", totals.openPos, "text-yellow-400"],
            ["Slabs In Stock", totals.slabs, "text-emerald-400"],
            ["Sinks On Hand", totals.sinks, "text-indigo-300"],
          ].map(([k, v, c]) => (
            <div
              key={k}
              data-testid={`suppliers-stat-${k.toLowerCase().replace(/ /g, "-")}`}
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            >
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                {k}
              </div>
              <div className={`font-mono-data mt-1 text-xl font-bold ${c}`}>
                {v}
              </div>
            </div>
          ))}
        </div>

        <FilterBar
          className="mt-6"
          testidPrefix="suppliers"
          search={{
            value: q,
            onChange: setQ,
            placeholder: "Search name, contact, phone…",
            testid: "supplier-search",
          }}
          sections={[
            {
              key: "sort",
              label: "Sort by",
              sort: true,
              chipPrefix: "Sorted: ",
              value: sort,
              allValue: "name",
              onChange: setSort,
              options: SORTS.map(([value, label]) => [
                value,
                label,
                `suppliers-sort-${value.replace(/_/g, "-")}`,
              ]),
            },
          ]}
          right={
            <div
              className="flex shrink-0 overflow-hidden rounded-md border border-zinc-800"
              data-testid="supplier-view-toggle"
            >
              {VIEWS.map((v) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`supplier-view-${v.toLowerCase()}`}
                  onClick={() => setView(v)}
                  className={`h-12 px-3 text-[11px] font-semibold uppercase tracking-widest transition-colors duration-150 ${
                    view === v
                      ? "bg-indigo-500 text-white"
                      : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          }
        />

        {filtered.length === 0 && (
          <div className="mt-4 rounded-lg border border-dashed border-zinc-800 p-10 text-center">
            <Truck size={26} weight="bold" className="mx-auto text-zinc-700" />
            <p className="mt-3 text-sm text-zinc-400">
              {items.length === 0
                ? "No suppliers yet."
                : "No suppliers match that search."}
            </p>
          </div>
        )}

        {view === "Cards" && filtered.length > 0 && (
          <div
            className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
            data-testid="suppliers-cards"
          >
            {paged.map((s) => (
              <Link
                key={s.id}
                to={`/inventory/suppliers/${s.id}`}
                data-testid={`supplier-card-${s.id}`}
                className="rounded-lg border border-zinc-800 bg-zinc-900 p-4 transition-colors duration-150 hover:border-indigo-400/40"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-bold">{s.name}</h3>
                    <p className="truncate text-xs text-zinc-500">
                      {[s.contact, s.phone].filter(Boolean).join(" · ") ||
                        "No contact yet"}
                    </p>
                  </div>
                  {s.open_pos > 0 && (
                    <span className="shrink-0 rounded-full border border-yellow-500/25 bg-yellow-500/10 px-2.5 py-1 text-[10px] uppercase tracking-widest text-yellow-400">
                      {s.open_pos} open
                    </span>
                  )}
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  {[
                    ["POs", s.po_count],
                    ["Slabs", s.slabs_in_stock],
                    ["Sinks", s.sinks_on_hand],
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      className="rounded-md border border-zinc-800 bg-zinc-950 p-2"
                    >
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {k}
                      </div>
                      <div className="font-mono-data text-sm font-bold">
                        {v}
                      </div>
                    </div>
                  ))}
                </div>
                {s.next_arrival && (
                  <div className="mt-3 text-xs text-zinc-400">
                    <span className="text-zinc-600">Next arrival </span>
                    <span className="font-mono-data">{s.next_arrival}</span>
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}

        {view === "Table" && filtered.length > 0 && (
          <div
            className="mt-4 overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-900"
            data-testid="suppliers-table"
          >
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="p-3">Supplier</th>
                  <th className="p-3">Contact</th>
                  <th className="p-3">Phone</th>
                  <th className="p-3 text-right">POs</th>
                  <th className="p-3 text-right">Open</th>
                  <th className="p-3 text-right">Slabs</th>
                  <th className="p-3 text-right">Sinks</th>
                  <th className="p-3">Next arrival</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((s) => (
                  <tr
                    key={s.id}
                    data-testid={`supplier-row-${s.id}`}
                    className="border-b border-zinc-800 last:border-0"
                  >
                    <td className="p-3 font-semibold">
                      <Link
                        to={`/inventory/suppliers/${s.id}`}
                        className="hover:text-yellow-400"
                        data-testid={`supplier-open-${s.id}`}
                      >
                        {s.name}
                      </Link>
                    </td>
                    <td className="p-3 text-zinc-400">{s.contact || "—"}</td>
                    <td className="font-mono-data p-3 text-zinc-400">
                      {s.phone || "—"}
                    </td>
                    <td className="font-mono-data p-3 text-right">
                      {s.po_count}
                    </td>
                    <td className="font-mono-data p-3 text-right text-yellow-400">
                      {s.open_pos || "—"}
                    </td>
                    <td className="font-mono-data p-3 text-right text-emerald-400">
                      {s.slabs_in_stock}
                    </td>
                    <td className="font-mono-data p-3 text-right text-indigo-300">
                      {s.sinks_on_hand}
                    </td>
                    <td className="font-mono-data p-3 text-zinc-400">
                      {s.next_arrival || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={curPage} total={filtered.length} onPage={setPage} testId="suppliers-pagination" />
      </main>
    </div>
  );
}
